const neutral = {
  bg: '#f4f5f8',
  panel: '#ffffff',
  panelAlt: '#f7f8fb',
  line: '#e0e3e9',
  lineStrong: '#ccd0d7',
  grid: '#e5e8ec',
  inkFaint: '#adb2ba',
  inkMute: '#818893',
  inkSoft: '#52565e',
  ink: '#171a1f',
  /** Text colour for use on saturated/accent backgrounds (brand primary,
   *  semantic error/warn/success/info). Pure white in both themes — the
   *  underlying accent colours are designed for white foreground contrast. */
  inkOnAccent: '#ffffff',
} as const;

const neutralDark = {
  bg: '#171a1e',
  panel: '#1c1e24',
  panelAlt: '#20232a',
  line: '#2c2f38',
  lineStrong: '#393d48',
  grid: '#292d36',
  inkFaint: '#585d6c',
  inkMute: '#818893',
  inkSoft: '#a2a8ae',
  ink: '#e8ecf2',
  inkOnAccent: '#ffffff',
} as const;

const brand = {
  primary: '#ef7335',
  primaryHover: '#d4571b',
  primarySoft: '#fdece1',
  primaryLine: '#f5c9a9',
} as const;

const brandDark = {
  primary: '#ef7335',
  primaryHover: '#f07838',
  primarySoft: '#2a1e16',
  primaryLine: '#5a3520',
} as const;

const semantic = {
  error: { color: '#c5392b', soft: '#fbe7e3' },
  warn: { color: '#b77410', soft: '#fbefd8' },
  success: { color: '#3f7d4d', soft: '#e3f1e5' },
  info: { color: '#3f5e92', soft: '#e9eef7' },
} as const;

/**
 * Activity color slots for schedule visualizations (Gantt bars, timeline lanes,
 * trip cards). Slot names match dispatch-domain vocabulary because every
 * dispatch system — transit, logistics, field service, mining shift — uses
 * the same five categories: a passenger/load pickup, a passenger/load drop-off,
 * a planned break, a depot/yard event, and dead-head travel between these.
 *
 * Treat these as generic color slots, not transport-only labels. If a future
 * domain needs different categories, add a sibling palette rather than renaming
 * these.
 *
 * Slots:
 * - `fill` — soft tint for filled regions (Gantt bar background, zone fill).
 * - `bar`  — mid-tone for the bar itself, hairline strokes, route polylines.
 * - `ink`  — readable text/icon colour on a `fill` background.
 * - `marker` — bold, saturated tone for solid map markers (white icon on top).
 *   Kept distinct from `bar` so the schedule stays soft while map pins read at
 *   a glance and give white foregrounds real contrast. Shared light/dark.
 */
const activity = {
  pickup: { fill: '#cfe7d0', bar: '#7ab389', ink: '#2e6b3f', marker: '#2f9e57' },
  dropoff: { fill: '#cde1f5', bar: '#7aa9d6', ink: '#2d5f8f', marker: '#2f6fc4' },
  break: { fill: '#f0e8d0', bar: '#c5a85f', ink: '#6f5922', marker: '#c98a10' },
  depot: { fill: '#e5e0f5', bar: '#8e82b8', ink: '#47408a', marker: '#6a52c8' },
  deadhead: { fill: '#eaecef', bar: '#bcbfc4', ink: '#61666e', marker: '#8a8f98' },
  stop: { fill: '#e8eaee', bar: '#9aa0a8', ink: '#3f4248', marker: '#5f6670' },
} as const;

const status = {
  done: { color: '#61666e', bg: '#eceef2', dot: '#787f8a' },
  active: { color: '#ea6626', bg: '#fdece1', dot: '#ea6626' },
  upcoming: { color: '#3f5e92', bg: '#e9eef7', dot: '#7c96bc' },
  skipped: { color: '#7b818a', bg: '#e9ebee', dot: '#979ba3' },
  noShow: { color: '#c5392b', bg: '#fbe7e3', dot: '#c5392b' },
} as const;

export const lightColors = { neutral, brand, semantic, activity, status } as const;
export const darkColors = {
  neutral: neutralDark,
  brand: brandDark,
  semantic,
  activity,
  status,
} as const;

type Widen<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends Array<infer U>
      ? Array<Widen<U>>
      : T extends object
        ? { -readonly [K in keyof T]: Widen<T[K]> }
        : T;

export type ColorTokens = Widen<typeof lightColors>;
